/** Verified: docs/operations/clause-verifications/compliance-only-duties-2026-09-30.md. */
/**
 * Open disclosure internal follow-up clock.
 *
 * The register confirms there is no statutory open-disclosure commencement
 * clock (incidents: r 164-40(1)(b)(v), complaints: r 165-35(2) require an open disclosure process,
 * without a numeric timeframe) — Statura's 24h is an internal operational
 * target, TARGET.
 */
import type { ComplianceDeadlineItem } from './types';
import type { ComplianceDeadlineSettings } from '../compliance-settings';

const MS_PER_HOUR = 1000 * 60 * 60;
const PROVISION =
  'Statura operational target (24h) — Aged Care Rules 2025 r 164-40(1)(b)(v); complaints r 165-35(2) — no numeric timeframe';

const MATERIAL_SEVERITIES = new Set(['HIGH', 'CRITICAL', 'SERIOUS', 'SEVERE', 'MODERATE']);

export interface OpenDisclosureApplicabilityRow {
  severity: string | null;
  sirsEffectivePriority: 1 | 2 | null;
}

/**
 * Restricts open disclosure reminders to incidents that materially trigger
 * the obligation: SIRS-reportable (P1/P2) or moderate-or-above severity.
 */
export function isOpenDisclosureMaterial(row: OpenDisclosureApplicabilityRow): boolean {
  const sevHigh = MATERIAL_SEVERITIES.has(String(row.severity ?? '').toUpperCase());
  const sirsHigh = row.sirsEffectivePriority === 1 || row.sirsEffectivePriority === 2;
  return sevHigh || sirsHigh;
}

export interface OpenDisclosureProgressRow {
  openDisclosureStatus: string | null;
  openDisclosureDate: string | null;
}

/**
 * How long a disclosure recorded as in progress pauses the follow-up reminders.
 * Provider policy set by the owner on 9 October 2026 ("7 days, then resume"),
 * not a legal clock. The OPEN_DISCLOSURE escalation chain applies the same
 * number in process_escalation_timers() (migration 20435000001305).
 */
export const OPEN_DISCLOSURE_COMMENCED_PAUSE_DAYS = 7;

const MS_PER_DAY = 24 * MS_PER_HOUR;

/**
 * The 24h target is a commencement target: it asks for the conversation to be
 * started and recorded. A record showing a disclosure in progress with a
 * meeting date in the last 7 days meets it and pauses the follow-up reminders.
 * After 7 days without COMPLETED or DECLINED the reminders resume, so a
 * disclosure that was started and abandoned is chased again. A scheduled
 * (future) disclosure, or progress with no date, is not evidence of
 * commencement and keeps the reminder.
 */
export function isOpenDisclosureCommenced(row: OpenDisclosureProgressRow, now: Date): boolean {
  if (row.openDisclosureStatus !== 'IN_PROGRESS' || !row.openDisclosureDate) return false;
  const disclosedMs = new Date(row.openDisclosureDate).getTime();
  if (!Number.isFinite(disclosedMs)) return false;
  const ageMs = now.getTime() - disclosedMs;
  return ageMs >= 0 && ageMs < OPEN_DISCLOSURE_COMMENCED_PAUSE_DAYS * MS_PER_DAY;
}

/** When the pause for a commenced disclosure ends and reminders resume. */
export function openDisclosureCommencedPauseEndsAt(openDisclosureDate: string): string {
  return new Date(
    new Date(openDisclosureDate).getTime() + OPEN_DISCLOSURE_COMMENCED_PAUSE_DAYS * MS_PER_DAY,
  ).toISOString();
}

export interface OpenDisclosureRow {
  id: string;
  discoveredAt: string;
}

/** Approaching between 20h and 24h after discovery. */
export function evaluateOpenDisclosureApproaching(
  row: OpenDisclosureRow,
  now: Date,
  settings?: ComplianceDeadlineSettings,
): { item: ComplianceDeadlineItem } | null {
  const targetHours = settings?.open_disclosure_target ?? 24;
  const warningHours = Math.max(0, targetHours - 4);
  const discoveredMs = new Date(row.discoveredAt).getTime();
  const hoursElapsed = (now.getTime() - discoveredMs) / MS_PER_HOUR;
  if (hoursElapsed < warningHours || hoursElapsed > targetHours) return null;
  const dueAt = new Date(discoveredMs + targetHours * MS_PER_HOUR).toISOString();
  return {
    item: {
      id: `open-disclosure:${row.id}`,
      register: 'OPEN_DISCLOSURE',
      recordRef: row.id,
      title: 'Open disclosure 24h target approaching',
      dueAt,
      kind: 'TARGET',
      provisionOrTargetLabel: PROVISION,
      status: 'DUE_SOON',
      href: `/compliance/open-disclosure/${row.id}`,
      meta: { hoursElapsed },
    },
  };
}

/** Missed once more than 24h has elapsed since discovery. */
export function evaluateOpenDisclosureMissed(
  row: OpenDisclosureRow,
  now: Date,
  settings?: ComplianceDeadlineSettings,
): { item: ComplianceDeadlineItem } | null {
  const targetHours = settings?.open_disclosure_target ?? 24;
  const discoveredMs = new Date(row.discoveredAt).getTime();
  const hoursElapsed = (now.getTime() - discoveredMs) / MS_PER_HOUR;
  if (hoursElapsed <= targetHours) return null;
  const dueAt = new Date(discoveredMs + targetHours * MS_PER_HOUR).toISOString();
  return {
    item: {
      id: `open-disclosure-missed:${row.id}`,
      register: 'OPEN_DISCLOSURE',
      recordRef: row.id,
      title: 'Open disclosure 24h target missed',
      dueAt,
      kind: 'TARGET',
      provisionOrTargetLabel: PROVISION,
      status: 'MISSED',
      href: `/compliance/open-disclosure/${row.id}`,
      meta: { hoursElapsed },
    },
  };
}
